import { useCallback, useContext, useRef } from 'react'

import { IconCloudArrowUp, Textarea } from '@vtex/shoreline'

import { Button } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { readCodesCsv } from '../../../helpers/gtin'
import styles from '../styles.module.css'

const NOT_FOUND_PREVIEW = 8

/** A rota do BFF aceita até 500 EANs e até 500 SKUs por busca. */
export const BATCH_MAX_CODES = 500
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024

export type BatchSummary = {
  found: number
  notFound: string[]
}

export type BatchPanelProps = {
  rawGtins: string
  onRawGtinsChange: (value: string) => void
  /** Marca de cada código lida da coluna Marca do CSV enviado. */
  onFileBrandsChange: (brands: Record<string, string>) => void
  /** Falso quando a marca é decidida fora da lista (nova marca): a coluna Marca do arquivo é ignorada. */
  withBrands: boolean
  parsedCount: number
  /** Mais códigos do que a busca aceita: o botão não libera. */
  isOverLimit?: boolean
  isSearching: boolean
  onSearch: () => void
  summary?: BatchSummary
}

/** Colagem/CSV de EANs e SKUs, busca sob demanda e resumo do que o catálogo devolveu. */
export const BatchPanel: React.FC<BatchPanelProps> = ({
  rawGtins,
  onRawGtinsChange,
  onFileBrandsChange,
  withBrands,
  parsedCount,
  isOverLimit = false,
  isSearching,
  onSearch,
  summary
}) => {
  const { t } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]

      event.target.value = ''

      if (!file) return

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.critical(
          t('rm:advertiserProducts.addModal.batch.fileTooBig', {
            max: MAX_FILE_SIZE_BYTES / (1024 * 1024)
          })
        )

        return
      }

      const { text, brands } = readCodesCsv(await file.text(), withBrands)

      onFileBrandsChange(brands)

      onRawGtinsChange(rawGtins.trim() ? `${rawGtins}\n${text}` : text)
    },
    [onFileBrandsChange, onRawGtinsChange, rawGtins, t, toast, withBrands]
  )

  return (
    <div className={styles.batchPanel}>
      <Textarea
        value={rawGtins}
        onChange={onRawGtinsChange}
        placeholder={t('rm:advertiserProducts.addModal.batch.placeholder')}
        style={{ minHeight: 88 }}
      />

      <div className={styles.batchActions}>
        <span className={styles.batchHint}>
          {t(
            isOverLimit
              ? 'rm:advertiserProducts.addModal.batch.tooMany'
              : 'rm:advertiserProducts.addModal.batch.toSearch',
            { count: parsedCount, max: BATCH_MAX_CODES }
          )}
        </span>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.tsv,text/csv,text/plain"
          hidden
          onChange={handleFile}
        />
        <Button
          template="transparent"
          size="small"
          iconLeft={<IconCloudArrowUp width={16} height={16} />}
          text={t('rm:advertiserProducts.addModal.batch.upload')}
          onClick={() => fileInputRef.current?.click()}
        />

        <Button
          template="primary"
          size="small"
          disabled={!parsedCount || isOverLimit}
          loading={isSearching}
          onClick={onSearch}
        >
          {t('rm:advertiserProducts.addModal.batch.cta')}
        </Button>
      </div>

      {!!summary && (
        <div className={styles.batchSummary}>
          <span>
            {t('rm:advertiserProducts.addModal.batch.found', {
              count: summary.found
            })}
          </span>

          {!!summary.notFound.length && (
            <span className={styles.batchSummaryWarn}>
              {t('rm:advertiserProducts.addModal.batch.notFound', {
                count: summary.notFound.length
              })}
              : {summary.notFound.slice(0, NOT_FOUND_PREVIEW).join(', ')}
              {summary.notFound.length > NOT_FOUND_PREVIEW ? '…' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
